/**
 * Material UI Theme Configuration
 * Professional admin dashboard theme with brand colors
 */
import { createTheme, ThemeOptions } from '@mui/material/styles';
import { palette } from './palette';

// Common theme options
const commonOptions: ThemeOptions = {
  typography: {
    fontFamily: '"Inter", "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
          '&:hover': {
            background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.light.border}`,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: palette.light.muted,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'light',
    primary: {
      main: palette.brand[500],
      light: palette.brand[400],
      dark: palette.brand[600],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: palette.brandSecondary[500],
      light: palette.brandSecondary[400],
      dark: palette.brandSecondary[600],
      contrastText: '#FFFFFF',
    },
    error: {
      main: palette.status.danger,
      dark: palette.status.dangerDark,
    },
    warning: {
      main: palette.status.warning,
      dark: palette.status.warningDark,
    },
    success: {
      main: palette.status.success,
      dark: palette.status.successDark,
    },
    info: {
      main: palette.status.info,
      dark: palette.status.infoDark,
    },
    background: {
      default: palette.light.background,
      paper: palette.light.card,
    },
    text: {
      primary: palette.light.foreground,
      secondary: palette.light.mutedForeground,
    },
    divider: palette.light.border,
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: palette.brand[500],
      light: palette.brand[400],
      dark: palette.brand[600],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: palette.brandSecondary[500],
      light: palette.brandSecondary[400],
      dark: palette.brandSecondary[600],
      contrastText: '#FFFFFF',
    },
    error: {
      main: palette.status.danger,
      dark: palette.status.dangerDark,
    },
    warning: {
      main: palette.status.warning,
      dark: palette.status.warningDark,
    },
    success: {
      main: palette.status.success,
      dark: palette.status.successDark,
    },
    info: {
      main: palette.status.info,
      dark: palette.status.infoDark,
    },
    background: {
      default: palette.dark.background,
      paper: palette.dark.card,
    },
    text: {
      primary: palette.dark.foreground,
      secondary: palette.dark.mutedForeground,
    },
    divider: palette.dark.border,
  },
  components: {
    ...commonOptions.components,
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.dark.border}`,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: palette.dark.muted,
        },
      },
    },
  },
});

